import { appData, state, getBoardViewSettings } from "../../app/state/state.js";
import {
  compareTopLevelItemsByField,
  getResolvedCardDate,
} from "./itemSortUtils.js";
import { getBoardRegistryEntry } from "./boardRegistry.js";
import {
  getBoardFilterDefinitions,
  getBoardFilterOptions,
  getBoardSortOptions,
  resolveBoardPriorityLabel,
  resolveBoardStatusLabel,
  resolveBoardTypeNames,
} from "./boardRuntimeOptions.js";
import {
    findItem,
} from "../utils/itemLookup.js";
import {
    filterTasksByVisibilityHorizon,
} from "../utils/taskVisibility.js";
import {
    getPatientProviderDisplayNames,
} from "../utils/providerUtils.js";
import {
    getTaskDisplayUserIds,
    getUsersTasks,
} from "../utils/taskDisplay.js";
import {
    getUserDisplayNameById,
    getUserInitials,
} from "../utils/userDisplay.js";
import {
    isItemClosed,
} from "../utils/closedState.js";

/**
 * Returns sort options registered for a board view.
 * @param {string} viewKey Board view key.
 * @returns {Array<object>} Sort option definitions.
 */
export function getSortOptions(viewKey) {
  return getBoardSortOptions(viewKey);
}

/**
 * Returns filter definitions registered for a board view.
 * @param {string} viewKey Board view key.
 * @returns {Array<object>} Filter definitions.
 */
export function getFilterDefinitions(viewKey) {
  return getBoardFilterDefinitions(viewKey);
}

/**
 * Finds one filter definition for a board view.
 * @param {string} viewKey Board view key.
 * @param {string} filterKey Filter key.
 * @returns {object | null} Filter definition, or null.
 */
export function getFilterDefinition(viewKey, filterKey) {
  return getFilterDefinitions(viewKey).find((definition) => definition.key === filterKey) || null;
}

/**
 * Returns available options for one board filter.
 * @param {string} viewKey Board view key.
 * @param {string} filterKey Filter key.
 * @returns {Array<object>} Filter option definitions.
 */
export function getFilterOptions(viewKey, filterKey) {
  return getBoardFilterOptions(viewKey, filterKey);
}

function getSettings(viewKey) {
  return getBoardViewSettings(viewKey) || {};
}

function getResolvedSortBy(viewKey, sortBy) {
  if (!sortBy) {return null;}
  return getSortOptions(viewKey).some((option) => option.key === sortBy) ?
    sortBy :
    null;
}

function getItemTypeForViewKey(viewKey) {
  return getBoardRegistryEntry(viewKey)?.itemType || "";
}

/**
 * Checks whether a board view has active filters.
 * @param {string} viewKey Board view key.
 * @returns {boolean} True when any filter has a selected value.
 */
export function hasActiveFilters(viewKey) {
  return getFilterDefinitions(viewKey).some((definition) =>
    getFilterValueCount(getSettings(viewKey).filters?.[definition.key], definition) > 0,
  );
}

/**
 * Checks whether a board view has an active search query.
 * @param {string} viewKey Board view key.
 * @returns {boolean} True when search text is active.
 */
export function hasActiveSearch(viewKey) {
  return Boolean((getSettings(viewKey).searchQuery || "").trim());
}

/**
 * Checks whether sort, filter, or search controls are active.
 * @param {string} viewKey Board view key.
 * @returns {boolean} True when any runtime control is active.
 */
export function hasActiveRuntimeControls(viewKey) {
  const settings = getSettings(viewKey);
  return Boolean(getResolvedSortBy(viewKey, settings.sortBy)) ||
    hasActiveFilters(viewKey) ||
    hasActiveSearch(viewKey);
}

/**
 * Counts selected values in a filter state value.
 * @param {*} value Filter state value.
 * @param {object} [definition] Filter definition.
 * @returns {number} Number of selected filter values.
 */
export function getFilterValueCount(value, definition = null) {
  if (!value) {return 0;}
  if (Array.isArray(value)) {
    if (definition?.kind === "date") {
      return value[0] || value[1] ? 1 : 0;
    }
    return value.filter(Boolean).length;
  }
  return 0;
}

/**
 * Counts all active filter selections for a board view.
 * @param {string} viewKey Board view key.
 * @returns {number} Total active filter value count.
 */
export function getTotalActiveFilterCount(viewKey) {
  const settings = getSettings(viewKey);
  return getFilterDefinitions(viewKey).reduce((total, definition) =>
    total + getFilterValueCount(settings.filters?.[definition.key], definition), 0);
}

function getLinkedTasks(item, taskList = []) {
  if (!item?.id) {return [];}
  return (taskList || []).filter((task) => task?.parentId === item.id);
}

function appendText(target, ...values) {
  values.flat().forEach((value) => {
    if (value === null || value === undefined) {return;}
    if (typeof value === "string" && value.trim() === "") {return;}
    target.push(String(value));
  });
}

function buildTaskSearchText(task) {
  const values = [];
  const parent = task?.parentId ? findItem(task.parentId) : null;
  const userIds = getTaskDisplayUserIds(task);

  appendText(
    values,
    task?.title,
    task?.description,
    task?.latestNote?.text,
    task?.notesSearchText,
    parent?.name,
    resolveBoardStatusLabel("task", task),
    resolveBoardTypeNames("task", task?.types),
    getUserDisplayNameById(task?.owner),
    (task?.assigned_users || []).map((userId) => getUserDisplayNameById(userId)),
    userIds.map((userId) => getUserInitials(userId)),
  );

  return values.join("\n").toLowerCase();
}

function buildParentSearchText(itemType, item, taskList = []) {
  const values = [];
  const linkedTasks = getLinkedTasks(item, taskList);

  appendText(
    values,
    item?.name,
    item?.comments,
    item?.notesSearchText,
    resolveBoardStatusLabel(itemType, item),
    resolveBoardTypeNames(itemType, item?.types),
    linkedTasks.map((task) => task?.title),
    linkedTasks.map((task) => task?.latestNote?.text),
  );

  if (itemType === "patient") {
    appendText(
      values,
      item?.case_file_number,
      getUserDisplayNameById(item?.case_manager),
      getUserInitials(item?.case_manager),
      getPatientProviderDisplayNames(item?.provider || []),
      (item?.provider || []).map((providerId) => getUserInitials(providerId)),
      resolveBoardPriorityLabel(itemType, item),
    );
  }

  if (itemType === "project") {
    const managerIds = Array.isArray(item?.project_manager) ? item.project_manager : [];
    appendText(
      values,
      managerIds.map((userId) => getUserDisplayNameById(userId)),
      managerIds.map((userId) => getUserInitials(userId)),
      resolveBoardPriorityLabel(itemType, item),
    );
  }

  if (itemType === "asset") {
    appendText(
      values,
      item?.serial_number,
      item?.description,
      item?.supplier,
      item?.installer,
      (item?.issue_log || []).map((issue) => [issue?.issue, issue?.issue_details, issue?.repair_details, issue?.technician]),
      (item?.service_log || []).map((entry) => entry?.technician),
    );
  }

  if (itemType === "maintenance") {
    appendText(
      values,
      item?.item,
      item?.stream,
      item?.details,
      item?.repair_details,
      item?.technician,
      getUserDisplayNameById(item?.managed_by),
      getUserInitials(item?.managed_by),
      resolveBoardPriorityLabel(itemType, item),
    );
  }

  if (itemType === "lab") {
    appendText(
      values,
      item?.patient_name,
      item?.details,
      item?.lab,
      item?.stream,
      item?.latestNote?.text,
      item?.notesSearchText,
      getPatientProviderDisplayNames(item?.provider || []),
      (item?.provider || []).map((providerId) => getUserInitials(providerId)),
      (item?.assigned_users || []).map((userId) => getUserDisplayNameById(userId)),
      (item?.assigned_users || []).map((userId) => getUserInitials(userId)),
      resolveBoardPriorityLabel(itemType, item),
    );
  }

  return values.join("\n").toLowerCase();
}

/**
 * Builds the searchable text corpus for a board item.
 * @param {string} itemType Parent-board item type.
 * @param {object} item Item record.
 * @param {Array<object>} [taskList=[]] Related task list.
 * @returns {string} Lowercase searchable text.
 */
export function getSearchableText(itemType, item, taskList = []) {
  if (itemType === "task") {return buildTaskSearchText(item);}
  return buildParentSearchText(itemType, item, taskList);
}

function getFilterComparableValues(itemType, item, filterKey, taskList = []) {
  switch (filterKey) {
    case "types":
      return Array.isArray(item?.types) ? item.types.filter(Boolean) : [];
    case "priority":
      return item?.priority ? [item.priority] : [];
    case "status":
      return item?.status ? [item.status] : [];
    case "stream":
      return item?.stream ? [item.stream] : [];
    case "equipment_status":
      return item?.equipment_status ? [item.equipment_status] : [];
    case "owner":
      return item?.owner ? [item.owner] : [];
    case "assigned_users":
      return Array.isArray(item?.assigned_users) ? item.assigned_users.filter(Boolean) : [];
    case "lab":
      return item?.lab ? [item.lab] : [];
    case "case_manager":
      return item?.case_manager ? [item.case_manager] : [];
    case "provider":
      return Array.isArray(item?.provider) ? item.provider.filter(Boolean) : [];
    case "project_manager":
      return Array.isArray(item?.project_manager) ? item.project_manager.filter(Boolean) : [];
    case "managed_by":
      return item?.managed_by ? [item.managed_by] : [];
    case "technician":
      return item?.technician ? [item.technician] : [];
    case "subtask":
      return [item?.isSubtask ? "yes" : "no"];
    case "date": {
      const resolvedDate = getResolvedCardDate(itemType, item, taskList);
      return resolvedDate ? [resolvedDate] : [];
    }
    default:
      return [];
  }
}

function matchesDateFilter(itemType, item, filterValue, taskList = []) {
  const [rawFrom = "", rawTo = ""] = Array.isArray(filterValue) ? filterValue : ["", ""];
  const resolvedDate = getResolvedCardDate(itemType, item, taskList);
  if (!resolvedDate) {return false;}

  const comparableDate = String(resolvedDate).slice(0, 10);
  if (rawFrom && comparableDate < rawFrom) {return false;}
  if (rawTo && comparableDate > rawTo) {return false;}
  return true;
}

function matchesFilters(viewKey, itemType, item, filters = {}, taskList = []) {
  return getFilterDefinitions(viewKey).every((definition) => {
    const filterValue = filters?.[definition.key];
    if (getFilterValueCount(filterValue, definition) === 0) {return true;}

    if (definition.kind === "date") {
      return matchesDateFilter(itemType, item, filterValue, taskList);
    }

    const resolvedValues = getFilterComparableValues(
      itemType,
      item,
      definition.key,
      taskList,
    );
    return resolvedValues.some((value) => (filterValue || []).includes(String(value)));
  });
}

function getSourceItemsForView(viewKey) {
  switch (viewKey) {
    case "tasks": {
      const settings = getSettings(viewKey);
      const hasRuntimeControls = Boolean(getResolvedSortBy(viewKey, settings.sortBy)) ||
        hasActiveFilters(viewKey) ||
        hasActiveSearch(viewKey);
      const userTasks = getUsersTasks();
      return hasRuntimeControls ? userTasks : filterTasksByVisibilityHorizon(userTasks);
    }
    default: {
      const collectionName = getBoardRegistryEntry(viewKey)?.collectionName;
      return collectionName ? appData?.[collectionName] || [] : [];
    }
  }
}

function getWorkingItemsForFilterCounts(viewKey, omittedFilterKey = null) {
  const itemType = getItemTypeForViewKey(viewKey);
  const settings = getSettings(viewKey);
  const searchQuery = (settings.searchQuery || "").trim().toLowerCase();
  const taskList = appData.tasks;
  const nextFilters = { ...(settings.filters || {}) };

  if (omittedFilterKey) {
    delete nextFilters[omittedFilterKey];
  }

  let workingItems = getSourceItemsForView(viewKey)
    .filter((item) => state.showClosed || !isItemClosed(item));

  if (searchQuery) {
    workingItems = workingItems.filter((item) =>
      getSearchableText(itemType, item, taskList).includes(searchQuery),
    );
  }

  return workingItems.filter((item) =>
    matchesFilters(viewKey, itemType, item, nextFilters, taskList),
  );
}

/**
 * Counts matching board items for each option in a filter.
 * @param {string} viewKey Board view key.
 * @param {string} filterKey Filter key.
 * @returns {Map<string, number>} Option value to matching item count.
 */
export function getFilterOptionCounts(viewKey, filterKey) {
  const filterDefinition = getFilterDefinition(viewKey, filterKey);
  if (!filterDefinition || filterDefinition.kind === "date") {
    return new Map();
  }

  const itemType = getItemTypeForViewKey(viewKey);
  const taskList = appData.tasks;
  const counts = new Map();
  const workingItems = getWorkingItemsForFilterCounts(viewKey, filterKey);

  workingItems.forEach((item) => {
    const values = [...new Set(
      getFilterComparableValues(itemType, item, filterKey, taskList)
        .map((value) => String(value)),
    )];
    values.forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1);
    });
  });

  return counts;
}

/**
 * Applies configured search, filters, and sorting to board items.
 * @param {object} params Runtime pipeline parameters.
 * @param {string} params.viewKey Board view key.
 * @param {string} params.itemType Parent-board item type.
 * @param {Array<object>} [params.items=[]] Items to process.
 * @param {Array<object>} [params.taskList=appData.tasks] Related task list.
 * @returns {Array<object>} Processed board items.
 */
export function applyBoardRuntimePipeline({
  viewKey,
  itemType,
  items = [],
  taskList = appData.tasks,
}) {
  const settings = getSettings(viewKey);
  const filters = settings.filters || {};
  const searchQuery = (settings.searchQuery || "").trim().toLowerCase();
  const sortBy = getResolvedSortBy(viewKey, settings.sortBy);

  let workingItems = (items || []).filter((item) => state.showClosed || !isItemClosed(item));

  if (searchQuery) {
    workingItems = workingItems.filter((item) =>
      getSearchableText(itemType, item, taskList).includes(searchQuery),
    );
  }

  if (hasActiveFilters(viewKey)) {
    workingItems = workingItems.filter((item) =>
      matchesFilters(viewKey, itemType, item, filters, taskList),
    );
  }

  return [...workingItems].sort((a, b) => compareTopLevelItemsByField(
    itemType,
    a,
    b,
    sortBy,
    settings.sortDirection,
    taskList,
  ));
}
